import { HttpAction } from "@megaron/http-service";
import { baseDocSerializer, ResourceId, signalLevels, Signal, signalSerializer } from "@megaron/resource";
import { Serializers, SerializerValue } from "@megaron/serializers";

import { signalSubjectDocTypeSerializer } from "./signalSubjectDocType";

export { signalLevels, signalSerializer };
export type { Signal };
export type SignalLevel = (typeof signalLevels)[number];

export const defaultSignalInstanceKey = (signalType: string) => signalType;

export const signalInstanceKey = (mentionEventId: string, mentionedUser: string) =>
  `${mentionEventId}:${mentionedUser.toLowerCase()}`;

export const signalDocIdFromParts = (
  signalType: string,
  subjectDocId: string,
  instanceKey: string,
): ResourceId<"signal"> => {
  if (instanceKey === signalType || instanceKey === defaultSignalInstanceKey(signalType)) {
    return ResourceId("signal", `${signalType}:${subjectDocId}`);
  }

  return ResourceId("signal", `${signalType}:${subjectDocId}:${instanceKey}`);
};

export const signalDocSerializer = Serializers.object({
  ...baseDocSerializer("signal").fields,

  level: Serializers.stringOneOf(...signalLevels),
  app: Serializers.string,
  signalType: Serializers.string,
  instanceKey: Serializers.string.optional(),
  subjectDocType: signalSubjectDocTypeSerializer,
  subjectDocId: Serializers.string,
  createdAt: Serializers.datetime,
  isRead: Serializers.boolean.optional(),
  readAt: Serializers.datetime.nullable().optional(),
});

export type SignalDoc = SerializerValue<typeof signalDocSerializer>;

export const signalRefSerializer = Serializers.object({
  subjectDocType: signalSubjectDocTypeSerializer,
  subjectDocId: Serializers.string,
  signalType: Serializers.string,
  instanceKey: Serializers.string,
});

export type SignalRef = SerializerValue<typeof signalRefSerializer>;

export const signalDocRef = (signal: SignalDoc): SignalRef => ({
  subjectDocType: signal.subjectDocType,
  subjectDocId: signal.subjectDocId,
  signalType: signal.signalType,
  instanceKey: signal.instanceKey ?? defaultSignalInstanceKey(signal.signalType),
});

export const signalActions = {
  searchSignals: HttpAction({
    path: "/signals",
    method: "get",
    requiresAuth: true,
    qsSerializer: Serializers.object({
      app: Serializers.string,
      level: Serializers.stringOneOf(...signalLevels)
        .array()
        .asJsonString(),
      signalType: Serializers.string.array().asJsonString(),
      subjectDocType: signalSubjectDocTypeSerializer,
      subjectDocId: Serializers.string,
      limit: Serializers.integer,
      offset: Serializers.integer,
    }).partial(),
    valueSerializer: Serializers.paginatedList(signalDocSerializer),
  }),

  setSignalRead: HttpAction({
    path: "/signals/read",
    method: "put",
    requiresAuth: true,
    bodySerializer: Serializers.object({
      signals: signalRefSerializer.array(),
      isRead: Serializers.boolean,
    }),
  }),
};
